import React, { useState, useRef } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Stack, Button, Divider, Paper, Chip,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import * as XLSX from 'xlsx';
import PopoverFiltroRelatorio from './PopoverFiltroRelatorio';
import generatePdfRejeitados from './EditPdfRejeitados';
import { cores, coresGrafico } from '../styles/styleUtils';

const CORES = coresGrafico;

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');

// ── Proteções LGPD ────────────────────────────────────────────────────
const mascararCpfCnpj = (valor) => {
  if (!valor) return '—';
  const s = valor.replace(/\D/g, '');
  if (s.length === 11) return `***.${s.slice(3,6)}.${s.slice(6,9)}-**`;
  if (s.length === 14) return `**.${s.slice(2,5)}.${s.slice(5,8)}/***/***-**`;
  return '***';
};
const mascararTelefone = (v) => {
  if (!v) return '—';
  const s = v.replace(/\D/g, '');
  if (s.length >= 10) return `(${s.slice(0,2)}) ${s.slice(2,7)}-${s.slice(7,9)}**`;
  return '***';
};
const mascararData = () => '**/**/****';

export default function RelatorioRejeitadas({ data, filtro, setFiltro, onLimpar, type, isSupervisor }) {
  const [openFiltro, setOpenFiltro] = useState(null);

  const onCloseFiltro   = () => setOpenFiltro(null);
  const limparFiltro    = () => { setOpenFiltro(false); onLimpar(); };
  const onConfirmFiltro = (f) => { setOpenFiltro(false); setFiltro(f); };

  if (!data) return null;

  // Aplica filtros locais de consultor e produto
  const linhasFiltradas = data.linhas.filter((p) => {
    if (filtro?.consultor && p.user?._id !== filtro.consultor._id) return false;
    if (filtro?.produto   && p.produto?.nome !== filtro.produto)   return false;
    return true;
  });

  // Dados para gráficos
  const dadosMotivos = Object.entries(
    linhasFiltradas.reduce((acc, p) => {
      const m = p.motivoNome || 'Outros';
      acc[m] = (acc[m] || 0) + 1;
      return acc;
    }, {})
  )
    .map(([nome, quantidade]) => ({ nome, quantidade }))
    .sort((a, b) => b.quantidade - a.quantidade);

  const total = dadosMotivos.reduce((s, d) => s + d.quantidade, 0);
  const dadosPizza = dadosMotivos.map((d, i) => ({
    name: d.nome, value: d.quantidade,
    pct: total > 0 ? ((d.quantidade / total) * 100).toFixed(1) : '0',
    fill: CORES[i % CORES.length],
  }));

  const exportarExcel = () => {
    const wb = XLSX.utils.book_new();
    const sheet = [];
    sheet.push(['Relatório de Propostas Reprovadas']);
    sheet.push([]);
    sheet.push(['Nome do Cliente','Data da Proposta','Produto','Valor do Projeto','Consultor','Potência do Sistema','Motivo']);
    linhasFiltradas.forEach((p) => {
      sheet.push([
        p.cliente?.nome,
        p.data,
        p.produto?.nome || '—',
        isSupervisor ? p.valor : '***',
        p.user?.nome,
        p.kit?.potenciacc,
        p.motivoNome,
      ]);
    });
    sheet.push([]);
    const r = data.resumo;
    sheet.push(['Total Perdido (R$)',   isSupervisor ? r.somaValor : '***']);
    sheet.push(['Potência Total (kWp)', r.somaPotencia]);
    sheet.push(['Total de Projetos',    linhasFiltradas.length]);
    sheet.push(['% Reprovados',         `${Number(r.percentualReprovados || 0).toFixed(2)}%`]);
    sheet.push(['Motivo Principal',     r.maiorMotivo]);
    if (!isSupervisor) sheet.push(['⚠ Dados financeiros protegidos por LGPD — acesso restrito ao supervisor.']);
    const ws = XLSX.utils.aoa_to_sheet(sheet);
    XLSX.utils.book_append_sheet(wb, ws, 'Reprovadas');
    XLSX.writeFile(wb, 'relatorio-propostas-reprovadas.xlsx');
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" gap={2} mb={2}
        sx={{ '@media print': { display: 'none' } }}>
        <Button variant="outlined" onClick={() => setOpenFiltro(true)}>Filtro</Button>
        <Button variant="outlined" color="secondary" onClick={() => generatePdfRejeitados(data, filtro, isSupervisor)}>Exportar em PDF</Button>
        <Button variant="contained" onClick={exportarExcel}>Download Excel</Button>
      </Stack>

      {!isSupervisor && (
        <Chip label="🔒 Dados sensíveis protegidos por LGPD — exibição parcial para este perfil"
          color="warning" sx={{ mb: 2, width: '100%', borderRadius: 1 }} />
      )}

      <Paper elevation={2} sx={{ p: 4, maxWidth: 900, mx: 'auto',
        '@media print': { boxShadow: 'none', p: 2 } }}>

        {/* Cabeçalho */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Box>
            <Typography variant="h6" fontWeight="bold">Relatório de Propostas Reprovadas</Typography>
            <Typography variant="caption" color="text.secondary">
              {filtro?.datainicial && `De ${new Date(filtro.datainicial).toLocaleDateString('pt-BR')}`}
              {filtro?.datafinal  && ` até ${new Date(filtro.datafinal).toLocaleDateString('pt-BR')}`}
              {filtro?.consultor  && ` — Consultor: ${filtro.consultor.nome}`}
              {filtro?.produto    && ` — Produto: ${filtro.produto}`}
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ mb: 3 }} />

        {/* KPIs */}
        <Stack direction="row" gap={2} mb={3} flexWrap="wrap">
          {[
            { label: 'Total de Projetos', valor: fmtNum(data.resumo.totalRejeitados) },
            { label: 'Total Perdido',     valor: isSupervisor ? fmtBRL(data.resumo.somaValor) : '🔒 Restrito' },
            { label: 'Potência Total',    valor: `${Number(data.resumo.somaPotencia || 0).toFixed(2)} kWp` },
            { label: '% Reprovados',      valor: `${Number(data.resumo.percentualReprovados || 0).toFixed(2)}%` },
            { label: 'Ticket Médio',      valor: isSupervisor ? fmtBRL(data.resumo.ticketMedioValor) : '🔒 Restrito' },
            { label: 'Motivo Principal',  valor: data.resumo.maiorMotivo || '—' },
          ].map(({ label, valor }) => (
            <Box key={label} sx={{ flex: 1, minWidth: 120, textAlign: 'center',
              border: `1px solid ${cores.laranja}`, borderRadius: 1, p: 1, backgroundColor: '#FFF3E0' }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="subtitle1" fontWeight="bold" color={cores.laranja}>{valor}</Typography>
            </Box>
          ))}
        </Stack>

        {/* Gráfico de barras */}
        <Typography variant="subtitle1" fontWeight="bold" mb={1}>Reprovas por Motivo</Typography>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={dadosMotivos} layout="vertical" margin={{ left: 20, right: 40 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" allowDecimals={false} />
            <YAxis type="category" dataKey="nome" width={180} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => [`${v} propostas`, 'Quantidade']} />
            <Bar dataKey="quantidade" radius={[0, 4, 4, 0]}>
              {dadosMotivos.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <Typography variant="caption" color="text.secondary" mb={1} display="block">
          Total de propostas rejeitadas: <strong>{total}</strong>
        </Typography>

        {/* Gráfico de pizza */}
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={dadosPizza} dataKey="value" nameKey="name"
              cx="50%" cy="50%" outerRadius={100}
              label={({ name, pct }) => `${pct}%`}>
              {dadosPizza.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
            </Pie>
            <Legend />
            <Tooltip formatter={(v, name) => [
              `${v} (${dadosPizza.find(d => d.name === name)?.pct}%)`, name
            ]} />
          </PieChart>
        </ResponsiveContainer>

        <Divider sx={{ my: 2 }} />

        {/* Tabela */}
        <Typography variant="subtitle1" fontWeight="bold" mb={1}>Propostas Reprovadas</Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
              <TableCell><strong>Nome do Cliente</strong></TableCell>
              <TableCell><strong>Data</strong></TableCell>
              <TableCell><strong>Produto</strong></TableCell>
              {isSupervisor && <TableCell align="right"><strong>Valor do Projeto</strong></TableCell>}
              <TableCell><strong>Consultor</strong></TableCell>
              <TableCell align="right"><strong>Potência (kWp)</strong></TableCell>
              <TableCell><strong>Motivo</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {linhasFiltradas.map((p, i) => (
              <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF3E0' } }}>
                <TableCell>{p.cliente?.nome}</TableCell>
                <TableCell>{p.data}</TableCell>
                <TableCell>{p.produto?.nome || '—'}</TableCell>
                {isSupervisor && (
                  <TableCell align="right">{fmtBRL(p.valor)}</TableCell>
                )}
                <TableCell>{p.user?.nome}</TableCell>
                <TableCell align="right">{Number(p.kit?.potenciacc || 0).toFixed(2)}</TableCell>
                <TableCell>{p.motivoNome}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ mt: 3, mb: 1 }} />
        <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
          Motivo Principal: <strong>{data.resumo.maiorMotivo}</strong> — Gerado em {new Date().toLocaleDateString('pt-BR')}
          {!isSupervisor && ' — 🔒 Dados financeiros restritos por LGPD'}
        </Typography>
      </Paper>

      {openFiltro && (
        <PopoverFiltroRelatorio filtro={filtro} open={openFiltro}
          onClose={onCloseFiltro} onLimpar={limparFiltro}
          onConfirm={onConfirmFiltro} type={type} />
      )}
    </Box>
  );
}